import { create } from 'zustand';
import moment from 'moment-timezone';
import session from '../utils/session';
import { getUsersByCompanyId } from '../reducers/user/resources';
import { getCompanyGroups, createGroupV3, getGroupContactsV3 } from '../reducers/group/resources';
import { getSupportedTimeZones, createCampaign, validateCampaignV3, draftCampaignV3, getCampaignV3, updateDraftCampaignV3, sendDraftCampaignV3 } from '../reducers/campaign/resources';
import { importContactsExecuteV3, getCompanyCustomContactFieldsV3 } from '../reducers/contact/resources';
import { getUserModuleGroups, getUserCalendarTimeSlots } from '../reducers/modules/resources';
import { checkUserGroupDefault } from '../reducers/apps/resources';
import { getUserSettings } from '../reducers/integrations/resources';
import constants from '../constants';
import config from '../config';
import apiCore from '../utils/api-core';

const initialState = {
  loading: false,
  submitting: false,
  savingDraft: false,
  step: 0,
  showImportModal: false,
  showPreflightModal: false,
  validationResult: null,
  draftId: null,
  scheduleLapsed: false,
  userHasCalendar: false,
  userCalendarSettings: null,
  // Display-only metadata about the user's default calendar connection
  // (Cronofy account email + selected calendar name). Pulled from the
  // /apps/check-default response so the preflight modal can surface
  // *which* account the user has connected, not just "Connected".
  userCalendarConnection: null,
  previewTerminal: false,
  previewResetKey: 0,
  previewSimulateNoSlots: false,
  previewAudience: {
    loading: false,
    contacts: [],
    total: 0,
    index: 0,
    source: null, // 'group' | 'import' | null
  },
  calendarPreview: {
    loading: false,
    schedules: [],
    availableSlotCount: 0,
  },
  groups: [],
  users: [],
  moduleGroups: [],
  timezones: [],
  // Distinct `contactdetail.field` names for the company. Merged into the
  // static mailmerge whitelist so company-specific custom fields (CSV-imported
  // columns, integration-populated tokens like `redirecturl`) aren't flagged
  // as typos by the broadcast composer's pre-send validator.
  customContactFields: [],
  form: {
    campaignName: '',
    campaignPrivacy: false,
    message: '',
    noSlotsMessage: '',
    scheduleType: 'now',
    scheduleDate: null,
    timezone: '',
    moduleGroupId: '',
    messageType: 'custom',
    notificationUsers: [],
    audienceMode: 'group',
    groupId: '',
    groupName: '',
    groupPrivacy: false,
    importOption: 'replace',
    importedContacts: [],
    importedContacts: [],
    importedExistingContacts: [],
    shortenUrls: true,
    moduleResponseOverrides: {},
    availabilitySettings: null,
    // Scheduling-manager: which calendar(s) interviews book onto.
    //   schedulingMode  'single' | 'round_robin'
    //   schedulingUserId  single owner (defaults to self)
    //   schedulingUserIds round-robin set of teammates
    //   schedulingStrategy 'least_loaded' | 'even_split'
    schedulingMode: 'single',
    schedulingUserId: undefined,
    schedulingUserIds: [],
    schedulingStrategy: 'least_loaded',
  },
};

// Scheduling-manager: append the chosen calendar owner(s) to an outgoing payload.
// Self-only single bookings send nothing (backend defaults to the campaign owner),
// so plain campaigns are unaffected and never trigger the feature gate.
const applySchedulingOwner = (payload, form) => {
  const userData = session.get('userData') || {};
  const currentUserId = String(userData.id || '');
  if (form.schedulingMode === 'round_robin') {
    if (form.schedulingUserIds && form.schedulingUserIds.length) {
      payload.scheduling_user_ids = form.schedulingUserIds;
      payload.scheduling_strategy = form.schedulingStrategy || 'least_loaded';
    }
  } else if (form.schedulingUserId && String(form.schedulingUserId) !== currentUserId) {
    payload.scheduling_user_id = form.schedulingUserId;
  }
  return payload;
};

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.rows)) return value.rows;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const normalizeImportedContacts = (mappedContacts = []) => {
  const validNew = [];
  const existing = [];

  mappedContacts.forEach((contact) => {
    if (!contact) return;
    if (contact.isDuplicate && contact.existingId) {
      existing.push({
        id: contact.existingId,
        firstname: contact.firstname || '',
        lastname: contact.lastname || '',
        mobile: contact.mobile || '',
      });
      return;
    }

    if (contact.isValid && !contact.isDuplicate) {
      validNew.push({
        firstname: contact.firstname || '',
        lastname: contact.lastname || '',
        mobile: contact.mobile || '',
        email: contact.email || '',
      });
    }
  });

  return { validNew, existing };
};

const buildValidatePayload = (form) => {
  const payload = {
    campaign_name: form.campaignName || `${moment().format('MM/DD/YYYY HH:mm:ss')} Campaign`,
    campaign_privacy: form.campaignPrivacy ? 'private' : 'public',
    message: form.message || '',
    no_slots_message: form.noSlotsMessage || '',
    schedule_type: form.scheduleType === 'scheduled' ? 1 : 0,
    modulegroup_id: form.moduleGroupId || undefined,
    users_to_notify: form.notificationUsers.length ? { users: form.notificationUsers } : undefined,
    shorten_urls: form.shortenUrls,
    module_response_overrides: Object.keys(form.moduleResponseOverrides || {}).length > 0
      ? form.moduleResponseOverrides
      : undefined,
    availability_settings: form.availabilitySettings || undefined,
  };

  payload.timezone = form.timezone || '';
  if (form.scheduleType === 'scheduled') {
    payload.schedule = form.scheduleDate ? moment(form.scheduleDate).toISOString() : '';
  } else {
    payload.guessed_timezone = moment.tz.guess(true);
  }

  if (form.audienceMode === 'group') {
    payload.group_id = form.groupId || undefined;
  } else {
    payload.group_name = form.groupName || '';
    payload.group_privacy = form.groupPrivacy ? 'private' : 'public';
    payload.import_option = form.importOption;
    payload.contacts = form.importedContacts;
    payload.existing_contacts = form.importedExistingContacts;
  }

  return payload;
};

const buildCreatePayload = (form) => {
  const payload = {
    campaign_name: form.campaignName || `${moment().format('MM/DD/YYYY HH:mm:ss')} Campaign`,
    campaign_privacy: form.campaignPrivacy ? 'private' : 'public',
    message: form.message || '',
    no_slots_message: form.noSlotsMessage || '',
    schedule_type: form.scheduleType === 'scheduled' ? 1 : 0,
    group_privacy: form.groupPrivacy ? 'private' : 'public',
    timezone: form.timezone || '',
  };

  if (form.scheduleType === 'scheduled') {
    payload.schedule = form.scheduleDate ? moment(form.scheduleDate).toISOString() : '';
  } else {
    payload.guessed_timezone = moment.tz.guess(true);
  }

  if (form.moduleGroupId) {
    payload.modulegroup_id = form.moduleGroupId;
  }

  if (Object.keys(form.moduleResponseOverrides || {}).length > 0) {
    payload.module_response_overrides = JSON.stringify(form.moduleResponseOverrides);
  }

  if (form.availabilitySettings) {
    payload.availability_settings = JSON.stringify(form.availabilitySettings);
  }

  applySchedulingOwner(payload, form);

  if (form.notificationUsers.length) {
    payload.users_to_notify = { users: form.notificationUsers };
  }

  if (form.audienceMode === 'group') {
    payload.group_id = form.groupId;
  } else {
    payload.group_name = form.groupName || '';
    payload.import_option = form.importOption || 'replace';
    payload.contacts = form.importedContacts || [];
    payload.existing_contacts = form.importedExistingContacts || [];
    payload.selected_fields = JSON.stringify(['Firstname', 'Lastname', 'Mobile', 'Email']);
    payload.deleted_rows = JSON.stringify([]);
  }

  return payload;
};

const useCampaignCreateStore = create((set, get) => ({
  ...initialState,

  initialize: async (draftCampaignId = null, templateDraft = null, preselectedGroupId = null) => {
    set({ ...initialState, loading: true });
    try {
      const userData = session.get('userData') || {};
      const companyId = userData.companyId || userData.company_id;
      const currentUserId = String(userData.id || '');

      let draftCampaign = null;
      if (draftCampaignId) {
        try {
            const res = await getCampaignV3(draftCampaignId);
            draftCampaign = res.data;
        } catch (err) {
            console.error('Failed to load draft campaign', err);
        }
      }

      const [groupsRes, usersRes, timezonesRes, moduleGroupsRes, calendarRes, userSettingsRes, customFieldsRes] = await Promise.all([
        getCompanyGroups(companyId),
        getUsersByCompanyId(companyId),
        getSupportedTimeZones(),
        getUserModuleGroups(),
        checkUserGroupDefault('calendar'),
        getUserSettings().catch(() => null),
        getCompanyCustomContactFieldsV3().catch(() => null),
      ]);

      const groups = asArray(groupsRes?.data);
      const users = asArray(usersRes?.data);
      const timezones = asArray(timezonesRes?.data);
      const moduleGroups = asArray(moduleGroupsRes?.data);
      const userHasCalendar = (calendarRes?.data || []).length > 0;
      const userCalendarSettings = userHasCalendar ? (userSettingsRes?.data || null) : null;
      let userCalendarConnection = null;
      if (userHasCalendar) {
        const apps = calendarRes?.data || [];
        const cronofyApp = apps.find((a) => a?.app?.type === 'cronofy') || apps[0];
        const details = cronofyApp?.app?.calendar_details || null;
        userCalendarConnection = {
          email: details?.profile_name || null,
          calendarName: details?.calendar_name || null,
          providerName: details?.provider_name || null,
          authError: !!cronofyApp?.app?.auth_error,
        };
      }
      const customContactFields = Array.isArray(customFieldsRes?.data?.fields)
        ? customFieldsRes.data.fields
        : [];

      const currentOffset = moment().format('Z');
      const defaultTz = (timezones.find((tz) => tz.value === currentOffset) || {}).value || '';

      set((state) => {
        let draftFormValues = {};
        let scheduleLapsed = false;
        if (draftCampaign) {
          const rawSchedule = draftCampaign.schedule;
          const scheduledInPast = rawSchedule && moment(rawSchedule).isBefore(moment());

          draftFormValues = {
            campaignName: draftCampaign.name || '',
            message: draftCampaign.message || '',
            noSlotsMessage: draftCampaign.no_slots_message || '',
            scheduleType: rawSchedule && !scheduledInPast ? 'scheduled' : 'now',
            scheduleDate: rawSchedule && !scheduledInPast ? moment(rawSchedule).toISOString() : null,
            timezone: draftCampaign.timezone || defaultTz,
            audienceMode: draftCampaign.group_id ? 'group' : 'all',
            groupId: draftCampaign.group_id || undefined,
            moduleGroupId: draftCampaign.modulegroup_id || undefined,
            messageType: draftCampaign.modulegroup_id ? 'auto' : 'custom',
            campaignPrivacy: draftCampaign.privacy === 'private' || draftCampaign.privacy === true,
            shortenUrls: draftCampaign.shorten_urls !== false,
            moduleResponseOverrides: draftCampaign.module_response_overrides || {},
            availabilitySettings: draftCampaign.availability_settings || null,
            notificationUsers: Array.isArray(draftCampaign.users_to_notify)
                ? draftCampaign.users_to_notify.map(u => u.id || u)
                : (currentUserId ? [currentUserId] : []),
            schedulingMode: (Array.isArray(draftCampaign.scheduling_user_ids)
                ? draftCampaign.scheduling_user_ids.length > 0
                : !!draftCampaign.scheduling_user_ids) ? 'round_robin' : 'single',
            schedulingUserId: draftCampaign.scheduling_user_id || currentUserId || undefined,
            schedulingUserIds: Array.isArray(draftCampaign.scheduling_user_ids)
                ? draftCampaign.scheduling_user_ids
                : [],
            schedulingStrategy: draftCampaign.scheduling_strategy || 'least_loaded',
          };

          if (scheduledInPast) scheduleLapsed = true;
        } else if (templateDraft) {
          draftFormValues = {
            campaignName: templateDraft.campaignName || '',
            message: templateDraft.message || '',
            messageType: templateDraft.messageType || 'custom',
            moduleGroupId: templateDraft.moduleGroupId || undefined,
          };
        }

        // Pre-select group if navigated from groups page
        if (preselectedGroupId && !draftCampaign) {
          draftFormValues.audienceMode = 'group';
          draftFormValues.groupId = preselectedGroupId;
        }

        return {
          loading: false,
          userHasCalendar,
          userCalendarSettings,
          userCalendarConnection,
          groups,
          users,
          timezones,
          moduleGroups,
          customContactFields,
          draftId: draftCampaign ? draftCampaign.id : null,
          scheduleLapsed,
          form: {
            ...state.form,
            timezone: defaultTz,
            notificationUsers: currentUserId ? [currentUserId] : [],
            schedulingUserId: currentUserId || undefined,
            ...draftFormValues
          },
        };
      });

      // If the restored draft uses the default interview module group and has no
      // saved overrides, prefill from the user's calendar settings.
      const { form } = get();
      if (
        form.moduleGroupId &&
        (!form.moduleResponseOverrides || Object.keys(form.moduleResponseOverrides).length === 0)
      ) {
        get().prefillCalendarOverrides(form.moduleGroupId);
      }
    } catch (error) {
      set({ loading: false });
      throw error;
    }
  },

  setStep: (step) => set({ step }),
  nextStep: () => set((state) => ({ step: Math.min(state.step + 1, 2) })),
  prevStep: () => set((state) => ({ step: Math.max(state.step - 1, 0) })),
  setShowImportModal: (showImportModal) => set({ showImportModal }),
  setShowPreflightModal: (showPreflightModal) => set({ showPreflightModal }),
  clearValidationResult: () => set({ validationResult: null }),

  setFormField: (field, value) =>
    set((state) => {
      const updates = { [field]: value };
      // Any change that would alter what the preview displays invalidates the
      // mandatory preview gate. The user must walk through the preview again.
      const previewInvalidatingFields = [
        'moduleGroupId', 'moduleResponseOverrides', 'availabilitySettings',
        'message', 'noSlotsMessage', 'messageType',
      ];
      const shouldInvalidatePreview = previewInvalidatingFields.includes(field);

      // Clear response overrides and calendar preview when switching module groups
      if (field === 'moduleGroupId') {
        updates.moduleResponseOverrides = {};
        updates.availabilitySettings = null;
        // Async: prefill overrides from user's calendar settings if this is the default module group
        if (value) {
          setTimeout(() => get().prefillCalendarOverrides(value), 0);
        }
        return {
          form: { ...state.form, ...updates },
          calendarPreview: { loading: false, schedules: [], availableSlotCount: 0 },
          previewTerminal: false,
        };
      }

      const patch = { form: { ...state.form, ...updates } };
      if (shouldInvalidatePreview) patch.previewTerminal = false;
      return patch;
    }),

  setPreviewTerminal: (v) => set({ previewTerminal: !!v }),
  resetPreview: () => set((state) => ({
    previewTerminal: false,
    previewResetKey: (state.previewResetKey || 0) + 1,
  })),

  setPreviewSimulateNoSlots: (next) => set((state) => ({
    previewSimulateNoSlots: !!next,
    previewTerminal: false,
    previewResetKey: (state.previewResetKey || 0) + 1,
  })),

  // Fetch the audience's contact list so the user can cycle through real
  // contacts when previewing — {{firstname}} etc. resolves against actual
  // DB rows. Falls back to import-mode contacts (in-memory) for not-yet-saved
  // groups. Triggered when the preflight modal opens.
  loadPreviewAudience: async () => {
    const { form } = get();
    set((state) => ({
      previewAudience: { ...state.previewAudience, loading: true },
      previewSimulateNoSlots: false,
    }));

    try {
      if (form.audienceMode === 'group' && form.groupId) {
        const res = await getGroupContactsV3(form.groupId, { page: 1, pageSize: 50 });
        const body = res?.data?.data || res?.data || {};
        const rows = Array.isArray(body.rows) ? body.rows : [];
        set({
          previewAudience: {
            loading: false,
            contacts: rows,
            total: body.total || rows.length,
            index: 0,
            source: 'group',
          },
        });
        return;
      }

      if (form.audienceMode === 'import') {
        // Imported contacts aren't in the DB yet — surface name/mobile only so
        // the cycler still shows who's being previewed. The backend will fall
        // back to the synthetic Preview User contact for templating.
        const imported = (form.importedContacts || []).concat(form.importedExistingContacts || []);
        set({
          previewAudience: {
            loading: false,
            contacts: imported.map((c, i) => ({
              id: c.id || null,
              name: `${c.firstname || ''} ${c.lastname || ''}`.trim() || `Contact ${i + 1}`,
              mobile: c.mobile,
              email: c.email,
            })),
            total: imported.length,
            index: 0,
            source: 'import',
          },
        });
        return;
      }

      set({
        previewAudience: {
          loading: false, contacts: [], total: 0, index: 0, source: null,
        },
      });
    } catch (err) {
      console.error('Failed to load preview audience', err);
      set({
        previewAudience: {
          loading: false, contacts: [], total: 0, index: 0, source: null,
        },
      });
    }
  },

  setPreviewContactIndex: (nextIndex) => set((state) => {
    const total = state.previewAudience.contacts.length;
    if (total === 0) return {};
    const clamped = ((nextIndex % total) + total) % total;
    return {
      previewAudience: { ...state.previewAudience, index: clamped },
      previewTerminal: false,
      previewResetKey: (state.previewResetKey || 0) + 1,
    };
  }),

  cyclePreviewContact: (direction) => set((state) => {
    const total = state.previewAudience.contacts.length;
    if (total === 0) return {};
    const cur = state.previewAudience.index || 0;
    const next = direction === 'prev' ? cur - 1 : cur + 1;
    const clamped = ((next % total) + total) % total;
    return {
      previewAudience: { ...state.previewAudience, index: clamped },
      previewTerminal: false,
      previewResetKey: (state.previewResetKey || 0) + 1,
    };
  }),

  // When the default calendar module group is selected, pre-fill modifiable
  // module responses with the user's saved interview settings.
  prefillCalendarOverrides: async (moduleGroupId) => {
    const defaultCronofyId = config.defaultInterviewModules?.cronofy;
    if (!moduleGroupId || moduleGroupId !== defaultCronofyId) return;

    try {
      const [settingsRes, mgRes] = await Promise.all([
        getUserSettings(),
        apiCore.get(`/api/v3/module-group/${moduleGroupId}`),
      ]);

      const settings = settingsRes?.data;
      const modules = (mgRes?.data || mgRes)?.modules || [];
      if (!settings || !modules.length) return;

      // Find the editable modules by reply_type or by being the start module
      const mgData = mgRes?.data || mgRes;
      const startModuleId = mgData.module_id;
      const overrides = {};

      for (const mod of modules) {
        if (!mod.response_user_modify) continue;

        if (mod.id === startModuleId && settings.inviteMsg) {
          // Start/Welcome module → user's invite message
          overrides[mod.id] = settings.inviteMsg;
        } else if (mod.reply_type === 'event' && settings.confirmationMsg) {
          // Event/booking confirmation module
          overrides[mod.id] = settings.confirmationMsg;
        } else if (
          mod.id === config.defaultInterviewModules?.cronofyPostEventConfirmation &&
          settings.postEventConfirmationResponseMessage
        ) {
          // End/Thank You module
          overrides[mod.id] = settings.postEventConfirmationResponseMessage;
        }
      }

      const updates = {};
      if (Object.keys(overrides).length > 0) {
        updates.moduleResponseOverrides = overrides;
      }
      if (settings.noSlotsAvailableMessage) {
        updates.noSlotsMessage = settings.noSlotsAvailableMessage;
      }

      if (Object.keys(updates).length > 0) {
        set((state) => ({
          form: { ...state.form, ...updates },
        }));
      }
    } catch (err) {
      console.error('Failed to prefill calendar overrides:', err);
    }
  },

  setAudienceMode: (audienceMode) =>
    set((state) => ({
      form: {
        ...state.form,
        audienceMode,
        groupId: audienceMode === 'group' ? state.form.groupId : '',
      },
    })),

  applyImportedContacts: (mappedContacts) => {
    const { validNew, existing } = normalizeImportedContacts(mappedContacts);
    set((state) => ({
      form: {
        ...state.form,
        audienceMode: 'import',
        groupId: '',
        importedContacts: validNew,
        importedExistingContacts: existing,
      },
      showImportModal: false,
    }));
  },

  confirmImportedGroup: async () => {
    const { form } = get();
    const groupName = (form.groupName || '').trim();
    if (!groupName) {
      return { ok: false, message: 'Please enter a group name' };
    }
    try {
      // 1. Create the group
      const res = await createGroupV3({
        name: groupName,
        privacy: form.groupPrivacy ? 'private' : 'public',
      });
      const newGroup = res?.data?.data || res?.data;
      const newGroupId = newGroup?.id;
      if (!newGroupId) {
        return { ok: false, message: 'Failed to create group' };
      }

      // 2. Import the contacts into the new group
      const allContacts = [
        ...(form.importedContacts || []),
        ...(form.importedExistingContacts || []),
      ];
      if (allContacts.length > 0) {
        await importContactsExecuteV3({
          contacts: allContacts,
          importStrategy: form.importOption || 'skip',
          groupId: newGroupId,
        });
      }

      // 3. Refresh groups list to get real contact count from DB
      const userData = session.get('userData') || {};
      const companyId = userData.companyId || userData.company_id;
      const groupsRes = await getCompanyGroups(companyId);
      const groups = asArray(groupsRes?.data);

      set((state) => ({
        groups,
        form: {
          ...state.form,
          audienceMode: 'group',
          groupId: newGroupId,
          // Clear imported contacts since they're now persisted in the DB
          importedContacts: [],
          importedExistingContacts: [],
        },
      }));

      return { ok: true, message: `Group "${groupName}" created with ${allContacts.length} contacts` };
    } catch (error) {
      return { ok: false, message: error?.response?.data?.message || 'Failed to create group' };
    }
  },

  refreshGroupsAndSelect: async (groupId) => {
    const userData = session.get('userData') || {};
    const companyId = userData.companyId || userData.company_id;
    const groupsRes = await getCompanyGroups(companyId);
    const groups = asArray(groupsRes?.data);
    set((state) => ({
      groups,
      showImportModal: false,
      form: {
        ...state.form,
        audienceMode: 'group',
        groupId,
        importedContacts: [],
        importedExistingContacts: [],
      },
    }));
  },

  getAudienceCount: () => {
    const { form, groups } = get();
    if (form.audienceMode === 'group') {
      const safeGroups = Array.isArray(groups) ? groups : [];
      const group = safeGroups.find((g) => String(g.id) === String(form.groupId));
      return parseInt(group?.contact_count || 0);
    }
    return (form.importedContacts || []).length + (form.importedExistingContacts || []).length;
  },

  validateCampaign: async () => {
    const { form } = get();
    set({ submitting: true });
    try {
      const validatePayload = buildValidatePayload(form);
      const validateRes = await validateCampaignV3(validatePayload);
      const validation = validateRes?.data || {};

      if (!validation.ok) {
        const first = (validation.errors || [])[0];
        return {
          ok: false,
          message: first?.message || 'Some field(s) need your attention.',
        };
      }

      set({ validationResult: { meta: validation.meta || {}, warnings: validation.warnings || [] } });
      return { ok: true };
    } catch (error) {
      const msg = error?.message || 'Failed to validate campaign';
      return { ok: false, message: msg };
    } finally {
      set({ submitting: false });
    }
  },

  submitCampaign: async () => {
    const { form, draftId, previewTerminal } = get();
    if (!previewTerminal) {
      return { ok: false, message: 'Walk through the live preview to enable Send' };
    }
    set({ submitting: true });
    try {
      const createPayload = buildCreatePayload(form);

      let response;
      if (draftId) {
        response = await sendDraftCampaignV3(draftId, createPayload);
      } else {
        response = await createCampaign(createPayload);
      }

      const body = response?.data || {};
      set({ ...initialState });
      return {
        ok: true,
        message: 'Campaign created successfully',
        contactCount: body.contactCount,
        excludedArchivedCount: body.excludedArchivedCount || 0,
      };
    } catch (error) {
      const msg = error?.message || 'Failed to create campaign';
      return { ok: false, message: msg };
    } finally {
      set({ submitting: false });
    }
  },

  saveAsDraft: async () => {
    const { form, draftId } = get();
    set({ savingDraft: true });
    try {
        const draftPayload = {
            campaign_name: form.campaignName || `${moment().format('MM/DD/YYYY HH:mm:ss')} Campaign`,
            campaign_privacy: form.campaignPrivacy,
            message: form.message,
            no_slots_message: form.noSlotsMessage,
            schedule: form.scheduleType === 'scheduled' && form.scheduleDate 
                ? moment(form.scheduleDate).utc().format('YYYY-MM-DD HH:mm:ss+00') 
                : moment().utc().format('YYYY-MM-DD HH:mm:ss+00'),
            timezone: form.timezone || moment.tz.guess(true),
            group_id: form.audienceMode === 'group' ? form.groupId : undefined,
            modulegroup_id: form.moduleGroupId || undefined,
            users_to_notify: form.notificationUsers.length ? form.notificationUsers : undefined,
            shorten_urls: form.shortenUrls,
            module_response_overrides: Object.keys(form.moduleResponseOverrides || {}).length > 0
                ? form.moduleResponseOverrides
                : undefined,
            availability_settings: form.availabilitySettings || undefined,
        };

        applySchedulingOwner(draftPayload, form);

        if (draftId) {
            await updateDraftCampaignV3(draftId, draftPayload);
        } else {
            await draftCampaignV3(draftPayload);
        }
        set({ ...initialState });
        return { ok: true, message: 'Campaign drafted successfully' };
    } catch (error) {
        return { ok: false, message: error?.response?.data?.message || 'Failed to save draft' };
    } finally {
        set({ savingDraft: false });
    }
  },

  fetchCalendarPreview: async (fromDate) => {
    set((state) => ({
      calendarPreview: { ...state.calendarPreview, loading: true },
    }));
    try {
      const { form, timezones } = get();
      const ianaZone = form.timezone
        ? ((timezones || []).find((t) => t.value === form.timezone) || {}).zone || moment.tz.guess(true)
        : moment.tz.guess(true);
      const res = await getUserCalendarTimeSlots(encodeURIComponent(fromDate), form.availabilitySettings, ianaZone);
      const rawSlots = res?.data || [];
      const schedules = rawSlots.map((calEvent, i) => {
        const isGhSlot = !!calEvent.ghslot;
        const isAvailable = isGhSlot && calEvent.available;
        const isUnavailable = isGhSlot && !calEvent.available;
        const startTime = moment(calEvent.start).format('h:mm a');
        const endTime = moment(calEvent.end).format('h:mm a');

        let bg = '#8c8c8c';
        if (isAvailable) bg = '#52c41a';
        else if (isUnavailable) bg = '#ff4d4f';

        return {
          id: String(i),
          title: isGhSlot ? `${calEvent.title} (${startTime} - ${endTime})` : calEvent.title,
          start: calEvent.start,
          end: calEvent.end,
          backgroundColor: bg,
          borderColor: bg,
          allDay: calEvent.category === 'allday',
          extendedProps: {
            type: isAvailable ? 'available' : isUnavailable ? 'unavailable' : 'user-event',
          },
        };
      });

      const availableSlotCount = schedules.filter(
        (e) => e.extendedProps.type === 'available'
      ).length;

      set({
        calendarPreview: { loading: false, schedules, availableSlotCount },
      });
    } catch (err) {
      console.error('Failed to fetch calendar preview', err);
      set({
        calendarPreview: { loading: false, schedules: [], availableSlotCount: 0 },
      });
    }
  },

  clearCalendarPreview: () =>
    set({ calendarPreview: { loading: false, schedules: [], availableSlotCount: 0 } }),

  reset: () => set({ ...initialState }),
}));

export const CAMPAIGN_MAX_MESSAGE_LENGTH = constants.maxMessageLength || 640;
export default useCampaignCreateStore;
